/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { TrashIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Theme } from '@nexigendigital/nxui'
import { useUpdateCartItemsMutation } from 'api/shop'
import { removeFromItemsByNameID } from 'store/slices/shopSlice'
import { OrderType, ProductServiceType, Weight } from 'models/enums'
import { CartItem } from 'models/shop'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { aud, titleCase } from 'helpers/utils'

import Configs from 'containers/shop/cart/configs'
import BillingCyclePricing from 'containers/shop/cart/billingCyclePricing'
import Addons from 'containers/shop/cart/addons'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function CartItems() {
    const dispatch = useAppDispatch()
    const { pathname } = useLocation()
    const { items, cart, cartUpdateStatus } = useAppSelector((state) => state.shop)
    const [updateCartItems] = useUpdateCartItemsMutation()

    function productType(item: CartItem): string {
        const pendingItem = items.find((pendingItem) => pendingItem.product_id === item.product_id && pendingItem.name === item.name)

        return pendingItem ? pendingItem.product_type : ''
    }

    return (
        <StyledCartItems>
            {cart?.items.map((item: CartItem) => {
                return (
                    <Item key={`${item.name}-${item.product_id}`}>
                        <Product>
                            <Description>
                                <Name>
                                    {item.name}
                                </Name>
                                <Type>
                                    {titleCase(productType(item))}{' '}{item.order_type === OrderType.All ? '' : titleCase(item.order_type)}
                                </Type>
                            </Description>
                            <Actions>
                                <Pricing>
                                    <Discount>
                                        {parseFloat(item.price.discount) > 0 && aud(parseFloat(item.price.subtotal))}
                                    </Discount>
                                    <BillingCyclePricing item={item} isDiscounted={parseFloat(item.price.discount) > 0}/>
                                </Pricing>
                                <Action>
                                    <button
                                        type={'button'}
                                        disabled={cartUpdateStatus === 'loading'}
                                        onClick={() => {
                                            if (cart) {
                                                const cartItem = cart.items.find(({ product_id, name }) => {
                                                    return product_id === item.product_id && name === item.name
                                                })

                                                if (cartItem?.item_uuid) {
                                                    dispatch(removeFromItemsByNameID({
                                                        product_id: item.product_id,
                                                        name: item.name,
                                                        pathname
                                                    }))
                                                    updateCartItems({
                                                        uuid: cart.uuid,
                                                        remove_items: [cartItem.item_uuid]
                                                    })
                                                }
                                            }
                                        }}
                                    >
                                        <TrashIcon/>
                                    </button>
                                </Action>
                            </Actions>
                        </Product>
                        <Configs item={item}/>
                        {(productType(item) === ProductServiceType.SharedHosting && item.addons) &&
                            <Addons addons={item.addons}/>
                        }
                    </Item>
                )
            })}
        </StyledCartItems>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const StyledCartItems = styled.div`
    display: grid;
    gap: 20px;
    font-size: .875rem;
    width: 100%;
`

const Item = styled.div`
    display: grid;
    border-bottom: 1px solid ${props => props.theme['gray--100']};
`

const Product = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        flex-wrap: wrap;
    }
`

const Description = styled.div`
    display: flex;
    gap: 4px;
`

const Type = styled.div`
    white-space: nowrap;
    ${Theme.Functions.setFont('Inter', Weight.Medium)};
    color: ${props => props.theme['icon--300']};
`

const Name = styled.div`
    ${Theme.Functions.setFont('Inter', Weight.Medium)};
    color: ${props => props.theme['text--300']};
`

const Actions = styled.div`
    display: flex;
`

const Pricing = styled.div`
    color: ${props => props.theme['text--300']};
    display: flex;
    align-items: center;
`

const Discount = styled.div`
    ${Theme.Functions.setFont('Inter', Weight.Medium)};
    text-decoration: line-through;
`

const Action = styled.div`
    display: flex;

    > button {
        > svg {
            width: 16px;
            height: 16px;
            color: ${props => props.theme['error--100']};
        }
    }
`
